import React, { useEffect, useState } from "react";
import { PageHeader, StatCard } from "../components/PageHeader";
import api, { formatApiError, formatINR } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash, TrendUp, TrendDown } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, PieChart, Pie, Cell } from "recharts";
import CustomFieldsSection from "../components/CustomFieldsSection";

export default function Finance() {
  const [txns, setTxns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    type: "income", category: "", amount: "", date: new Date().toISOString().slice(0, 10), description: "",
    custom_fields: {},
  });

  const load = async () => {
    const [{ data: t }, { data: s }, { data: st }] = await Promise.all([
      api.get("/finance/transactions"),
      api.get("/finance/summary"),
      api.get("/settings"),
    ]);
    setTxns(t); setSummary(s);
    setCategories((st.transaction_category || []).map((c) => c.value));
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/finance/transactions", { ...form, amount: parseFloat(form.amount) });
      toast.success("Transaction added");
      setShowForm(false);
      setForm({ ...form, category: "", amount: "", description: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this transaction?")) return;
    await api.delete(`/finance/transactions/${id}`);
    toast.success("Deleted"); load();
  };

  const expenseByCat = (summary?.by_category || []).filter((c) => c.type === "expense");

  return (
    <div>
      <PageHeader
        eyebrow="Ledger"
        title="Income & Expenditure"
        subtitle="Complete business ledger in Indian Rupee (₹)."
        actions={
          <button onClick={() => setShowForm(true)} data-testid="new-txn-btn"
                  className="brand-btn px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-2">
            <Plus size={16} weight="bold" /> New Entry
          </button>
        }
      />
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard label="Total Income" value={formatINR(summary?.total_income || 0)} testId="stat-total-income" />
          <StatCard label="Total Expense" value={formatINR(summary?.total_expense || 0)} testId="stat-total-expense" />
          <StatCard label="Net Balance" value={formatINR(summary?.net_balance || 0)} testId="stat-net" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          <div className="lg:col-span-2 bg-white border border-[#E5E7EB] p-6">
            <div className="label-caps">Monthly cashflow</div>
            <div className="font-display font-bold text-xl mt-1 mb-4">Income vs Expense (₹)</div>
            <div style={{ width: "100%", height: 300 }}>
              <ResponsiveContainer>
                <BarChart data={summary?.monthly || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                  <XAxis dataKey="month" stroke="#6B7280" fontSize={12} />
                  <YAxis stroke="#6B7280" fontSize={12} tickFormatter={(v) => `₹${v / 1000}k`} />
                  <Tooltip formatter={(v) => formatINR(v)} />
                  <Legend />
                  <Bar dataKey="income" fill="#106109" name="Income" />
                  <Bar dataKey="expense" fill="#DC2626" name="Expense" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white border border-[#E5E7EB] p-6">
            <div className="label-caps">Expenses by category</div>
            <div className="font-display font-bold text-xl mt-1 mb-4">Where the money goes</div>
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={expenseByCat} dataKey="amount" nameKey="category" outerRadius={80} label>
                    {expenseByCat.map((c) => (
                      <Cell key={`${c.type}-${c.category}`} fill={["#106109","#DC2626","#FBBF24","#10B981","#6B7280","#7C3AED","#0EA5E9","#F97316"][expenseByCat.indexOf(c) % 8]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => formatINR(v)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#E5E7EB] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <tr className="text-left">
                {["Date", "Type", "Category", "Description", "Amount (₹)", ""].map((h) => (
                  <th key={h} className="px-4 py-3 label-caps">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {txns.map((t) => (
                <tr key={t.id} className="border-b border-[#F3F4F6]" data-testid={`txn-row-${t.id}`}>
                  <td className="px-4 py-3">{t.date}</td>
                  <td className="px-4 py-3">
                    {t.type === "income" ? (
                      <span className="inline-flex items-center gap-1 text-[#10B981]"><TrendUp size={14} weight="bold" /> Income</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[#DC2626]"><TrendDown size={14} weight="bold" /> Expense</span>
                    )}
                  </td>
                  <td className="px-4 py-3">{t.category}</td>
                  <td className="px-4 py-3 text-[#6B7280]">{t.description}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold" style={{ color: t.type === "income" ? "#10B981" : "#DC2626" }}>
                    {t.type === "income" ? "+" : "−"} {formatINR(t.amount)}
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => del(t.id)} className="border border-[#E5E7EB] p-1.5 hover:bg-[#FEF2F2] text-[#DC2626]" data-testid={`delete-txn-${t.id}`}>
                      <Trash size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              {txns.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-[#9CA3AF]">No entries yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="txn-form-modal">
          <div className="bg-white w-full max-w-lg border border-[#E5E7EB]">
            <div className="border-b border-[#E5E7EB] px-6 py-4">
              <div className="label-caps">New</div>
              <h3 className="font-display font-bold text-xl">Add Transaction</h3>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {["income", "expense"].map((t) => (
                  <button type="button" key={t} onClick={() => setForm({ ...form, type: t })} data-testid={`txn-type-${t}`}
                          className={`p-3 border text-sm font-semibold ${form.type === t ? "border-[#106109] bg-[#F4F6FC] text-[#106109]" : "border-[#E5E7EB]"}`}>
                    {t.toUpperCase()}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label-caps block mb-1.5">Date</label>
                  <input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="label-caps block mb-1.5">Amount (₹)</label>
                  <input type="number" min="0" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="txn-form-amount" />
                </div>
              </div>
              <div>
                <label className="label-caps block mb-1.5">Category</label>
                <select required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="txn-form-category">
                  <option value="">Select…</option>
                  {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="label-caps block mb-1.5">Description</label>
                <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <CustomFieldsSection entity="transaction" values={form.custom_fields || {}}
                                     onChange={(v) => setForm({ ...form, custom_fields: v })} />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-2.5 text-sm border border-[#E5E7EB]">Cancel</button>
                <button type="submit" className="brand-btn flex-1 py-2.5 text-sm font-semibold" data-testid="txn-form-submit">Save Entry</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
